import { useLazyQuery } from "@apollo/client"
import {
  Button,
  Card,
  Intent,
  Menu,
  MenuItem,
  Position,
} from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomButtonGroup from "components/Atoms/CustomButtonGroup"
import CustomDateInput from "components/Atoms/CustomDateInput"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomPopover from "components/Atoms/CustomPopover"
import CustomSpinner from "components/Atoms/CustomSpinner"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import { BlockUserNote } from "components/Common/BlockUserNote"
import DynamicMultiSelector from "components/Molecules/DynamicMultiSelector"
import FormController from "components/Molecules/FormController"
import MutationOptions from "components/Organisms/MutationOptions"
import SectionSeperator from "components/Organisms/SectionSeperator"
import FileUploadDialog from "components/Templates/FileUploadDialog"
import { EDUCATION_TYPES } from "data/static.items"
import { EMPLOYMENT_TYPES } from "data/static.items"
// Queries and Mutations
import { ALL_DEPARTMENTS } from "graphql/selector.queries.gql"
import {
  ADD_UPDATE_SYSTEM_USER,
  SEARCH_SYSTEM_USER,
  SEND_USER_CREDENTIALS,
  SYSTEM_USER_GROUPS,
  SYSTEM_USER_LIST,
} from "graphql/system-user.gql"
import { useFormDisabled } from "hooks/useAuth"
import { useAuthorizationSimple } from "hooks/useAuthorizationStrict"
import { getErrors } from "hooks/useErrors"
import { cloneDeep, get, omit, pick, set } from "lodash-es"
import systemUserModel from "models/organisms/system-user.model"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AppToaster, { ErrorToast, SuccessToast } from "utils/AppToaster"
import { getId } from "utils/custom.utils"
import { graphqlError } from "utils/parseError"

const requiredKeys = [
  "id",
  "username",
  "firstName",
  "lastName",
  "number",
  "dateOfBirth",
  "customerId",
]
function UserRegistration() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [isFileUploadOpen, setUpload] = useState(false)
  const canSendCredentials = useAuthorizationSimple("vt_can_email_credentials")
  const [userDetails, changeUserDetails] = useState<any>({
    groups: [],
    departments: [],
  })
  const [getUserDetails, { loading }] = useLazyQuery(SEARCH_SYSTEM_USER, {
    onCompleted({ userDetails }) {
      changeUserDetails({
        ...userDetails,
        education: EDUCATION_TYPES.find(et => et.id === userDetails.education),
        metadata: {
          ...userDetails?.metadata,
          employmentTypeCurrentCompany: EMPLOYMENT_TYPES.find(
            et =>
              et.id?.toLowerCase() ===
              userDetails.metadata?.employmentTypeCurrentCompany
          ),
          attachments: userDetails.metadata?.attachments?.map(a => ({
            url: a,
          })),
        },
      })
    },
  })

  const [notificationType, setNotificationType] = useState(null)
  const [getData, { loading: credentialsLoading }] = useLazyQuery(
    SEND_USER_CREDENTIALS,
    {
      onCompleted({ sendUserCredentials }) {
        if (sendUserCredentials) {
          SuccessToast(
            `Credentials emailed to ${userDetails?.email}, Please check your inbox`
          )
        }
      },
    }
  )

  useEffect(() => {
    if (id && parseInt(id)) getUserDetails({ variables: { id } })
  }, [id, getUserDetails])

  const errors = useMemo(() => {
    return getErrors({ schema: systemUserModel, state: userDetails })
  }, [userDetails])

  const parsedVariables = useMemo(() => {
    return {
      ...pick(userDetails, requiredKeys),
      groups: userDetails.groups.map(g => getId(g)),
      departments: userDetails.departments.map(d => getId(d)),
      education: userDetails.education?.id,

      email: userDetails.email ?? "",
      metadata: {
        ...omit(userDetails.metadata, ["__typename"]),
        employmentTypeCurrentCompany:
          userDetails?.metadata?.employmentTypeCurrentCompany?.id,
        attachments: userDetails.metadata?.attachments?.map(a =>
          a.url ? a.url : a
        ),
      },
      // ...(userDetails.dateOfBirth && { dateOfBirth: userDetails.dateOfBirth })
    }
  }, [userDetails])
  function handleChange(path, value) {
    const userDetailsCopy = cloneDeep(userDetails)
    set(userDetailsCopy, path, value)
    changeUserDetails(userDetailsCopy)
  }

  const handleClick = useCallback(() => {
    const type = notificationType
    if (!userDetails.id) {
      return ErrorToast("Please make sure the user is registered")
    }

    if (
      (type === "EML" || !type) &&
      (!userDetails.email || errors.details?.email)
    ) {
      return ErrorToast("Please make sure the user has a valid email")
    }
    if (
      (type === "EML" || !type) &&
      (!userDetails.number || errors.details?.number)
    ) {
      return ErrorToast("Please make sure the user has a phone number")
    }
    if (loading) return

    getData({
      variables: {
        type: type ? type : undefined,
        userId: userDetails.id,
      },
    })
  }, [
    notificationType,
    getData,
    userDetails.id,
    userDetails.email,
    userDetails.number,
    errors.details,
    loading,
  ])
  function showToast({ addUser }) {
    const name = get(addUser, "user.fullName")
    return AppToaster.show({
      message: `User ${name} ${
        userDetails.id ? "Edited" : "Added"
      } successfully.`,
      intent: Intent.SUCCESS,
    })
  }

  const authDisabled = useFormDisabled("user", parsedVariables)
  const disabled = userDetails?.isStaff ? true : authDisabled

  if (loading) return <CustomSpinner />

  return (
    <Card className="vt-user-registration">
      <div className="mt-2 w-full flex items-center">
        <span>
          <Button
            icon="arrow-left"
            onClick={() => navigate(-1)}
            minimal={true}
          ></Button>
        </span>
        <h5 className="ml-3">{userDetails.id ? "Edit" : "New"} User</h5>
      </div>
      <div className="container mt-3">
        {
          userDetails.isBlocked && (
            <BlockUserNote remark={userDetails.blockRemark} />
          )
        }
        <FormController
          source={userDetails}
          errors={errors.details}
          onChange={handleChange}
          disableAll={true}
          disabled={disabled || userDetails.isBlocked}
        >
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-4">
              <StringInput
                big={true}
                label="First Name"
                helperText="eg. Vaibhav"
                path="firstName"
                required
              ></StringInput>
            </div>
            <div className="col-span-4">
              <StringInput
                big={true}
                label="Last Name"
                helperText="eg. Shelke"
                path="lastName"
                required
              ></StringInput>
            </div>
            <div className="col-span-4">
              <CustomDateInput
                big={true}
                fill={true}
                label="Date Of Birth"
                path="dateOfBirth"
                helperText="eg. 27/06/1985"
              ></CustomDateInput>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-4 mt-3">
            <div className="col-span-4">
              <StringInput
                big={true}
                label="Mobile Number"
                type="text"
                maxLength={13}
                path="number"
                helperText="eg. 9089875643"
                required
              ></StringInput>
            </div>
            <div className="col-span-4">
              <StringInput
                big={true}
                label="Username"
                type="text"
                path="username"
                helperText="a unique username required at the time of login"
                required
              ></StringInput>
            </div>
            <div className="col-span-4">
              <StringInput
                big={true}
                label="E-Mail"
                path="email"
                helperText="xyz@gmail.com"
              ></StringInput>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-4 mt-3">
            <div className="col-span-4">
              <DynamicMultiSelector
                fill
                label="Groups"
                helperText="eg. Farmer"
                addItem={true}
                query={SYSTEM_USER_GROUPS}
                big={true}
                path="groups"
              />
            </div>
            <div className="col-span-4">
              <DynamicMultiSelector
                fill
                label="Departments"
                helperText="eg. Grapes Export"
                query={ALL_DEPARTMENTS}
                big={true}
                path="departments"
              />
            </div>
          </div>
          <SectionSeperator title="Personal" />

          {/* 
interviewDate
joiningDate
icardIssuedDate
visitingCardIssuedDate
laptopIssuedDate
attachments */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-4">
              <StringInput
                label="Secondary Number"
                path="metadata.secondaryNumber"
              />
            </div>
            <div className="col-span-4">
              <StringInput
                label="Personal Email"
                path="metadata.personalEmail"
              />
            </div>
            <div className="col-span-4">
              <StringInput
                label="Institute"
                path="metadata.instituteName"
                helperText="Pune Univer"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-4">
              <StringInput
                label="Passing Year"
                helperText="eg. 2012"
                path="metadata.yearOfPassing"
              />
            </div>
            <div className="col-span-4">
              <StaticSelector
                big={true}
                items={EDUCATION_TYPES}
                label="Education"
                path="education"
              />
            </div>
            <div className="col-span-4">
              <CustomFormGroup
                label="Additional Photos"
                labelInfo={
                  <span className="vt-highlight vt-dark">
                    ({userDetails?.metadata?.attachments?.length || "No"} Photos
                    uploaded)
                  </span>
                }
              >
                <CustomButton
                  intent="primary"
                  fill
                  icon="cloud-upload"
                  text="Upload Photos"
                  onClick={e => setUpload(true)}
                />
              </CustomFormGroup>
              <FileUploadDialog
                path="metadata.attachments"
                isOpen={isFileUploadOpen}
                onClose={e => setUpload(false)}
              />
            </div>
          </div>
          <div className="grid grid-cols-12 gap-4 mt-2">
            <div className="col-span-4">
              <StringInput path="customerId" label="Customer Id" />
            </div>
            <div className="col-span-4">
              <StringInput
                path="metadata.district"
                label="District"
                helperText="eg. Nashik"
              />
            </div>
            <div className="col-span-4">
              <StringInput label="Address" path="metadata.address" />
            </div>
          </div>
          <SectionSeperator title="Previous Company" />
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-4">
              <StringInput
                label="Previous Organization"
                path="metadata.previousOrganisation"
              />
            </div>
            <div className="col-span-4">
              <StringInput label="Experience" path="metadata.experience" />
            </div>
            <div className="col-span-4">
              <CustomDateInput
                label="Resignation Date"
                path="metadata.resignationDate"
              />
            </div>
          </div>
          <SectionSeperator title="Current Company" />
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-4">
              <CustomDateInput
                label="Interview Date"
                path="metadata.interviewDate"
              />
            </div>
            <div className="col-span-4">
              <CustomDateInput
                label="Joining Date"
                path="metadata.joiningDate"
              />
            </div>
            <div className="col-span-4">
              <CustomDateInput
                label="I-Card Issued On"
                path="metadata.icardIssuedDate"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 gap-4 mt-2">
            <div className="col-span-4">
              <CustomDateInput
                label="Visiting card issued on "
                path="metadata.visitingCardIssuedDate"
              />
            </div>
            <div className="col-span-4">
              <CustomDateInput
                label="Laptop issued on"
                path="metadata.laptopIssuedDate"
              />
            </div>
            <div className="col-span-4">
              <CustomDateInput
                label="Resignation date  "
                path="metadata.resignationDateCurrentCompany"
              />
            </div>
            <div className="col-span-4">
              <StaticSelector
                big={true}
                items={EMPLOYMENT_TYPES}
                label="Employment Type"
                path="metadata.employmentTypeCurrentCompany"
              />
            </div>
          </div>
        </FormController>

        <hr />
        <div className="flex justify-between">
          <CustomButtonGroup>
            <Button
              onClick={handleClick}
              disabled={credentialsLoading || !canSendCredentials || userDetails.isBlocked}
              intent="success"
              text="Send Credentials"
            />
            <CustomPopover
              minimal
              position={Position.BOTTOM_LEFT}
              content={
                <Menu>
                  <MenuItem
                    active={notificationType === "EML"}
                    disabled={credentialsLoading || !canSendCredentials || userDetails.isBlocked}
                    onClick={e =>
                      setNotificationType(prev =>
                        prev === "EML" ? null : "EML"
                      )
                    }
                    text="Email Credentials"
                    icon="envelope"
                  />
                  <MenuItem
                    active={notificationType === "SMS"}
                    disabled={credentialsLoading || !canSendCredentials || userDetails.isBlocked}
                    onClick={e =>
                      setNotificationType(prev =>
                        prev === "SMS" ? null : "SMS"
                      )
                    }
                    text="SMS Credentials"
                    icon="document"
                  />
                </Menu>
              }
            >
              <Button
                text={
                  notificationType === "EML"
                    ? "Mail"
                    : notificationType === "SMS"
                    ? "SMS"
                    : null
                }
                disabled={credentialsLoading || !canSendCredentials || userDetails.isBlocked}
                intent="success"
                rightIcon="chevron-down"
              />
            </CustomPopover>
          </CustomButtonGroup>
          <MutationOptions
            disabled={disabled || userDetails.isBlocked}
            mutation={ADD_UPDATE_SYSTEM_USER}
            refetchQueries={[
              {
                query: SYSTEM_USER_LIST,
                variables: {
                  query: "",
                  groupName: "*",
                  skip: 0,
                  first: 13,
                },
              },
            ]}
            variables={parsedVariables}
            validate={() => {
              if (errors.latest) {
                AppToaster.show({
                  message: errors.latest,
                  intent: "danger",
                })
                return false
              }
              return true
            }}
            onError={error => {
              AppToaster.show({
                message: graphqlError(error.message),
                intent: Intent.DANGER,
              })
            }}
            onSave={data => {
              showToast(data)
              navigate(-1)
            }}
            onSaveAndAdd={data => {
              showToast(data)
              navigate("/d/registration/user/new")
              changeUserDetails({
                groups: [],
                departments: [],
              })
            }}
            onSaveAndEdit={data => {
              if (!parseInt(id)) {
                navigate(
                  `/d/registration/user/${get(data, "addUser.user.id")}`,
                  { replace: true }
                )
              }

              showToast(data)
            }}
          ></MutationOptions>
        </div>
      </div>
    </Card>
  )
}
export default UserRegistration
